import axios from 'axios';
import { authStorage } from '@/utils/authStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/thesis`;
const SCREEN = `${BACKEND_URL}/api/thesis/screening`;
const PLAN = `${BACKEND_URL}/api/thesis/plan`;
const INSIGHTS = `${BACKEND_URL}/api/thesis/insights`;

/** Download links (<a href>) cannot send headers — pass the token in the query. */
const dl = () => {
  const t = authStorage.getAuthorToken();
  return t ? `&access_token=${encodeURIComponent(t)}` : '';
};

/**
 * ThesisRAG API client.
 * Every call appends `user_id` (or sends it in the body) so the backend can
 * verify ownership.
 */
export const thesisApi = {
  // PROJECTS
  listProjects: async (userId) => {
    const { data } = await axios.get(`${API}/projects`, { params: { user_id: userId } });
    return data;
  },

  // ADMIN / PREMIUM
  getFeatureFlags: async (userId) => {
    const { data } = await axios.get(`${API}/me/feature-flags`, { params: { user_id: userId } });
    return data;
  },
  listPremiumSectionTypes: async (userId) => {
    const { data } = await axios.get(`${API}/premium/section-types`, { params: { user_id: userId } });
    return data;
  },
  premiumGenerateSection: async (userId, payload) => {
    const { data } = await axios.post(`${API}/premium/generate-section`, payload, {
      params: { user_id: userId }, timeout: 900000,
    });
    return data;
  },
  createProject: async (userId, payload) => {
    const { data } = await axios.post(`${API}/projects`, { ...payload, owner_id: userId }, {
      params: { user_id: userId },
    });
    return data;
  },
  getProject: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}`, { params: { user_id: userId } });
    return data;
  },
  updateProject: async (userId, projectId, payload) => {
    const { data } = await axios.put(`${API}/projects/${projectId}`, payload, {
      params: { user_id: userId },
    });
    return data;
  },
  deleteProject: async (userId, projectId) => {
    const { data } = await axios.delete(`${API}/projects/${projectId}`, { params: { user_id: userId } });
    return data;
  },

  // DOCUMENTS
  listDocuments: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/documents`, {
      params: { user_id: userId },
    });
    return data;
  },
  uploadDocument: async (userId, projectId, file, title, sourceType = 'upload') => {
    const form = new FormData();
    form.append('file', file);
    if (title) form.append('title', title);
    form.append('source_type', sourceType);
    const { data } = await axios.post(`${API}/projects/${projectId}/documents/upload`, form, {
      params: { user_id: userId },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,  // 5 minutes for large file uploads
    });
    return data;
  },
  deleteDocument: async (userId, projectId, docId) => {
    const { data } = await axios.delete(`${API}/projects/${projectId}/documents/${docId}`, {
      params: { user_id: userId },
    });
    return data;
  },

  // PUBMED
  pubmedSearch: async (userId, projectId, query, maxResults = 10, ingest = false) => {
    const { data } = await axios.post(`${API}/pubmed/search`,
      { project_id: projectId, query, max_results: maxResults, ingest },
      { params: { user_id: userId } });
    return data;
  },
  pubmedIngest: async (userId, projectId, pmids) => {
    const { data } = await axios.post(`${API}/pubmed/ingest`,
      { project_id: projectId, pmids },
      { params: { user_id: userId } });
    return data;
  },

  // CHAPTERS
  listChapters: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/chapters`, {
      params: { user_id: userId },
    });
    return data;
  },
  generateChapter: async (userId, projectId, chapterTitle, userPrompt, nChunks = 10, language = 'en', minWords = 3000, retrievalMode = 'hybrid', chapterId = null, allowPoolFallback = true, opts = {}) => {
    const { data } = await axios.post(`${API}/chapters/generate`,
      { project_id: projectId, chapter_title: chapterTitle, user_prompt: userPrompt, n_chunks: nChunks, language, min_words: minWords, retrieval_mode: retrievalMode, chapter_id: chapterId, allow_pool_fallback: allowPoolFallback, fast_mode: !!opts.fastMode },
      { params: { user_id: userId }, timeout: 600000 });
    return data;
  },
  getChapter: async (userId, chapterId) => {
    const { data } = await axios.get(`${API}/chapters/${chapterId}`, { params: { user_id: userId } });
    return data;
  },
  updateChapter: async (userId, chapterId, payload) => {
    const { data } = await axios.put(`${API}/chapters/${chapterId}`, payload, {
      params: { user_id: userId },
    });
    return data;
  },
  deleteChapter: async (userId, chapterId) => {
    const { data } = await axios.delete(`${API}/chapters/${chapterId}`, { params: { user_id: userId } });
    return data;
  },

  // EXPORTS — return URL strings so the browser can open/download directly
  exportUrl: (fmt, userId, projectId, author, aiDisclosure = true, parts = {}) => {
    const qs = new URLSearchParams({
      user_id: userId, author: author || 'Author',
      ai_disclosure: aiDisclosure ? 1 : 0,
      title_page: parts.titlePage ? 1 : 0,
      toc: parts.toc ? 1 : 0,
      abbreviations: parts.abbreviations ? 1 : 0,
      declaration: parts.declaration ? 1 : 0,
      figure_list: parts.figureList ? 1 : 0,
      table_list: parts.tableList ? 1 : 0,
    });
    return `${API}/projects/${projectId}/export/${fmt}?${qs.toString()}${dl()}`;
  },
  exportMarkdownUrl: (userId, projectId, author, aiDisclosure = true) =>
    `${API}/projects/${projectId}/export/markdown?user_id=${encodeURIComponent(userId)}&author=${encodeURIComponent(author || 'Author')}&ai_disclosure=${aiDisclosure ? 1 : 0}${dl()}`,
  exportHtmlUrl: (userId, projectId, author, aiDisclosure = true, parts = {}) =>
    thesisApi.exportUrl('html', userId, projectId, author, aiDisclosure, parts),
  exportDocxUrl: (userId, projectId, author, aiDisclosure = true, parts = {}) =>
    thesisApi.exportUrl('docx', userId, projectId, author, aiDisclosure, parts),
  exportPdfUrl: (userId, projectId, author, aiDisclosure = true, parts = {}) =>
    thesisApi.exportUrl('pdf', userId, projectId, author, aiDisclosure, parts),
  exportLatexUrl: (userId, projectId, author) =>
    `${API}/projects/${projectId}/export/latex?user_id=${encodeURIComponent(userId)}&author=${encodeURIComponent(author || 'Author')}${dl()}`,

  // GLOBAL LITERATURE SEARCH (OpenAlex · Semantic Scholar · Crossref · arXiv)
  literatureSearch: async (userId, payload) => {
    const { data } = await axios.post(`${API}/literature/search`, payload,
      { params: { user_id: userId }, timeout: 120000 });
    return data;
  },
  literatureIngest: async (userId, projectId, records) => {
    const { data } = await axios.post(`${API}/literature/ingest`,
      { project_id: projectId, records }, { params: { user_id: userId }, timeout: 180000 });
    return data;
  },
  citationStyles: async (userId, q = '') => {
    const { data } = await axios.get(`${API}/citations/styles`, { params: { user_id: userId, q } });
    return data;
  },
  claimConfidence: async (userId, payload) => {
    const { data } = await axios.post(`${API}/premium/claim-confidence`, payload,
      { params: { user_id: userId }, timeout: 180000 });
    return data;
  },
  fetchFullTexts: async (userId, projectId, batch = 5) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/fulltext-fetch`, { batch },
      { params: { user_id: userId }, timeout: 300000 });
    return data;
  },
  manuscriptIntake: async (userId, projectId, file, createSections = true, importRefs = true) => {
    const form = new FormData();
    form.append('file', file);
    form.append('create_sections', String(createSections));
    form.append('import_references', String(importRefs));
    const { data } = await axios.post(`${API}/projects/${projectId}/manuscript-intake`, form,
      { params: { user_id: userId }, timeout: 300000 });
    return data;
  },
  getManuscriptIntake: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/manuscript-intake`,
      { params: { user_id: userId } });
    return data;
  },
  manuscriptIntakeJob: async (userId, projectId, jobId) => {
    const { data } = await axios.get(
      `${API}/projects/${projectId}/manuscript-intake/jobs/${jobId}`,
      { params: { user_id: userId } });
    return data;
  },
  manuscriptIntakeJobs: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/manuscript-intake/jobs`,
      { params: { user_id: userId } });
    return data;
  },

  // WRITING PLAN
  listPlanTasks: async (userId, projectId) => {
    const { data } = await axios.get(`${PLAN}/projects/${projectId}/tasks`,
      { params: { user_id: userId } });
    return data;
  },
  createPlanTask: async (userId, projectId, payload) => {
    const { data } = await axios.post(`${PLAN}/projects/${projectId}/tasks`, payload,
      { params: { user_id: userId } });
    return data;
  },
  updatePlanTask: async (userId, taskId, payload) => {
    const { data } = await axios.patch(`${PLAN}/tasks/${taskId}`, payload,
      { params: { user_id: userId } });
    return data;
  },
  deletePlanTask: async (userId, taskId) => {
    const { data } = await axios.delete(`${PLAN}/tasks/${taskId}`,
      { params: { user_id: userId } });
    return data;
  },
  verifyPlanTask: async (userId, taskId) => {
    const { data } = await axios.post(`${PLAN}/tasks/${taskId}/verify`, {},
      { params: { user_id: userId } });
    return data;
  },
  planJob: async (userId, jobId) => {
    const { data } = await axios.get(`${PLAN}/jobs/${jobId}`, { params: { user_id: userId } });
    return data;
  },
  syncPlanTasks: async (userId, projectId) => {
    const { data } = await axios.post(`${PLAN}/projects/${projectId}/sync`, {},
      { params: { user_id: userId }, timeout: 120000 });
    return data;
  },

  // INSIGHTS — consensus meter + citation network
  startConsensus: async (userId, projectId, payload) => {
    const { data } = await axios.post(`${INSIGHTS}/projects/${projectId}/consensus`, payload,
      { params: { user_id: userId } });
    return data;
  },
  listConsensus: async (userId, projectId) => {
    const { data } = await axios.get(`${INSIGHTS}/projects/${projectId}/consensus`,
      { params: { user_id: userId } });
    return data;
  },
  startCitationNetwork: async (userId, projectId, payload = {}) => {
    const { data } = await axios.post(`${INSIGHTS}/projects/${projectId}/citation-network`,
      payload, { params: { user_id: userId } });
    return data;
  },
  getCitationNetwork: async (userId, projectId) => {
    const { data } = await axios.get(`${INSIGHTS}/projects/${projectId}/citation-network`,
      { params: { user_id: userId } });
    return data;
  },
  insightJob: async (userId, jobId) => {
    const { data } = await axios.get(`${INSIGHTS}/jobs/${jobId}`,
      { params: { user_id: userId } });
    return data;
  },

  // ZOTERO
  zoteroStatus: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/zotero/status`,
      { params: { user_id: userId } });
    return data;
  },
  zoteroConnect: async (userId, projectId, payload) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/zotero/connect`, payload,
      { params: { user_id: userId }, timeout: 60000 });
    return data;
  },
  zoteroSync: async (userId, projectId, full = false) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/zotero/sync`, { full },
      { params: { user_id: userId }, timeout: 300000 });
    return data;
  },
  zoteroDisconnect: async (userId, projectId) => {
    const { data } = await axios.delete(`${API}/projects/${projectId}/zotero`,
      { params: { user_id: userId } });
    return data;
  },

  // PRISMA SCREENING
  screeningSessions: async (userId, projectId) => {
    const { data } = await axios.get(`${SCREEN}/sessions`,
      { params: { user_id: userId, project_id: projectId } });
    return data;
  },
  createScreeningSession: async (userId, payload) => {
    const { data } = await axios.post(`${SCREEN}/sessions`, payload,
      { params: { user_id: userId }, timeout: 120000 });
    return data;
  },
  getScreeningSession: async (userId, sessionId) => {
    const { data } = await axios.get(`${SCREEN}/sessions/${sessionId}`,
      { params: { user_id: userId } });
    return data;
  },
  deleteScreeningSession: async (userId, sessionId) => {
    const { data } = await axios.delete(`${SCREEN}/sessions/${sessionId}`,
      { params: { user_id: userId } });
    return data;
  },
  addScreeningRecords: async (userId, sessionId, records) => {
    const { data } = await axios.post(`${SCREEN}/sessions/${sessionId}/records`, { records },
      { params: { user_id: userId }, timeout: 120000 });
    return data;
  },
  aiScreen: async (userId, sessionId) => {
    const { data } = await axios.post(`${SCREEN}/sessions/${sessionId}/ai-screen`, {},
      { params: { user_id: userId }, timeout: 60000 });
    return data;
  },
  smartQueue: async (userId, sessionId, limit = 5) => {
    const { data } = await axios.get(`${SCREEN}/sessions/${sessionId}/smart-queue`,
      { params: { user_id: userId, limit }, timeout: 60000 });
    return data;
  },
  setScreeningStopRule: async (userId, sessionId, stopAfter) => {
    const { data } = await axios.patch(`${SCREEN}/sessions/${sessionId}/stop-rule`,
      { stop_after: stopAfter }, { params: { user_id: userId } });
    return data;
  },
  updateScreeningRecord: async (userId, recordId, payload) => {
    const { data } = await axios.patch(`${SCREEN}/records/${recordId}`, payload,
      { params: { user_id: userId } });
    return data;
  },
  screeningFlowUrl: (userId, sessionId) =>
    `${SCREEN}/sessions/${sessionId}/flow.png?user_id=${encodeURIComponent(userId)}${dl()}`,
  saveScreeningFigure: async (userId, sessionId) => {
    const { data } = await axios.post(`${SCREEN}/sessions/${sessionId}/save-figure`, {},
      { params: { user_id: userId }, timeout: 120000 });
    return data;
  },
  screeningMethodsParagraph: async (userId, sessionId) => {
    const { data } = await axios.get(`${SCREEN}/sessions/${sessionId}/methods-paragraph`,
      { params: { user_id: userId } });
    return data;
  },
  ingestIncludedStudies: async (userId, sessionId) => {
    const { data } = await axios.post(`${SCREEN}/sessions/${sessionId}/ingest-included`, {},
      { params: { user_id: userId }, timeout: 300000 });
    return data;
  },

  // BIBLIOGRAPHY
  bibliographyUrl: (userId, projectId, fmt) =>
    `${API}/projects/${projectId}/bibliography?user_id=${encodeURIComponent(userId)}&fmt=${fmt}${dl()}`,

  // TABLE GENERATION
  generateTable: async (userId, projectId, topic, nChunks = 8, language = 'bg') => {
    const { data } = await axios.post(`${API}/projects/${projectId}/generate-table`,
      { project_id: projectId, topic, n_chunks: nChunks, language },
      { params: { user_id: userId }, timeout: 180000 });
    return data;
  },

  // VERSION HISTORY
  listVersions: async (userId, chapterId) => {
    const { data } = await axios.get(`${API}/chapters/${chapterId}/versions`, {
      params: { user_id: userId },
    });
    return data;
  },
  restoreVersion: async (userId, chapterId, versionId) => {
    const { data } = await axios.post(`${API}/chapters/${chapterId}/restore/${versionId}`, null, {
      params: { user_id: userId },
    });
    return data;
  },

  // AI REVIEWER MODE
  reviewChapter: async (userId, chapterId) => {
    const { data } = await axios.post(`${API}/chapters/${chapterId}/review`, null, {
      params: { user_id: userId }, timeout: 180000,
    });
    return data;
  },
  listReviews: async (userId, chapterId) => {
    const { data } = await axios.get(`${API}/chapters/${chapterId}/reviews`, {
      params: { user_id: userId },
    });
    return data;
  },
  applySuggestion: async (userId, chapterId, reviewId, issueIndex) => {
    const { data } = await axios.post(`${API}/chapters/${chapterId}/apply-suggestion`,
      { review_id: reviewId, issue_index: issueIndex },
      { params: { user_id: userId }, timeout: 180000 });
    return data;
  },

  // EQUATOR COMPLIANCE
  listGuidelines: async () => {
    const { data } = await axios.get(`${BACKEND_URL}/api/equator/guidelines`);
    return data;
  },
  getGuideline: async (guidelineId) => {
    const { data } = await axios.get(`${BACKEND_URL}/api/equator/guidelines/${guidelineId}`);
    return data;
  },
  checkCompliance: async (userId, chapterId, guidelineId, language = 'bg') => {
    const { data } = await axios.post(`${API}/chapters/${chapterId}/compliance`,
      { guideline_id: guidelineId, language },
      { params: { user_id: userId }, timeout: 180000 });
    return data;
  },
  fixComplianceItem: async (userId, chapterId, payload) => {
    // payload: { guideline_id, item_id, apply, language }
    const { data } = await axios.post(`${API}/chapters/${chapterId}/compliance/fix`,
      payload,
      { params: { user_id: userId }, timeout: 180000 });
    return data;
  },

  // EVIDENCE PROVENANCE
  retrieveEvidence: async (userId, projectId, query, nResults = 5) => {
    const { data } = await axios.post(`${API}/evidence/retrieve`,
      { project_id: projectId, query, n_results: nResults },
      { params: { user_id: userId }, timeout: 30000 });
    return data;
  },

  // CLAIM SAFETY
  safetyCheckText: async (userId, text, language = 'bg') => {
    const { data } = await axios.post(`${API}/safety/check`,
      { text, language },
      { params: { user_id: userId }, timeout: 60000 });
    return data;
  },
  safetyCheckChapter: async (userId, chapterId) => {
    const { data } = await axios.post(`${API}/chapters/${chapterId}/safety-check`, null, {
      params: { user_id: userId }, timeout: 60000,
    });
    return data;
  },

  // AI TRANSPARENCY PANEL
  getChapterTransparency: async (userId, chapterId) => {
    const { data } = await axios.get(`${API}/chapters/${chapterId}/transparency`, {
      params: { user_id: userId },
    });
    return data;
  },
  approveChapter: async (userId, chapterId, approved = true) => {
    const { data } = await axios.post(`${API}/chapters/${chapterId}/approve`,
      { approved },
      { params: { user_id: userId } });
    return data;
  },

  // INLINE CONTINUE WRITING (Jenni-style)
  continueWriting: async (userId, projectId, precedingText, language = 'en', maxSentences = 3, opts = {}) => {
    const { data } = await axios.post(`${API}/ai/continue`, {
      project_id: projectId,
      preceding_text: precedingText,
      language,
      max_sentences: maxSentences,
      instruction: opts.instruction || null,
      following_text: opts.followingText || null,
    }, { params: { user_id: userId }, timeout: 60000 });
    return data;
  },

  // FINDINGS TABLE EXTRACTOR (Elicit-style multi-paper PICO)
  extractFindingsTable: async (userId, projectId, topic, docIds = [], pubmedIds = [], language = 'en') => {
    const { data } = await axios.post(`${API}/ai/findings-table`, {
      project_id: projectId,
      topic,
      doc_ids: docIds,
      pubmed_ids: pubmedIds,
      language,
    }, { params: { user_id: userId }, timeout: 120000 });
    return data;
  },

  // EQUATOR GUIDELINE SUGGESTER (Project Creation Wizard)
  suggestGuidelines: async (userId, projectType, description, language = 'en') => {
    const { data } = await axios.post(`${API}/projects/suggest-guidelines`, {
      project_type: projectType,
      description,
      language,
    }, { params: { user_id: userId }, timeout: 60000 });
    return data;
  },

  // ---- WAVE B v2 ----

  // Whole-article generator (Research Article / Case Report / Editorial / etc.)
  generateWholeArticle: async (userId, projectId, payload) => {
    const { data } = await axios.post(`${API}/articles/generate`,
      { project_id: projectId, ...payload },
      { params: { user_id: userId }, timeout: 600000 });
    return data;
  },

  // Selection-based AI transform (expand / shorten / rewrite / academic / translate / add_citation)
  transformText: async (userId, projectId, action, text, language = 'en') => {
    const { data } = await axios.post(`${API}/ai/transform`,
      { project_id: projectId, action, text, language },
      { params: { user_id: userId }, timeout: 90000 });
    return data;
  },

  // Generate a single metadata field (research_question / title / abstract / etc.)
  generateField: async (userId, projectId, field, language = 'en') => {
    const { data } = await axios.post(`${API}/ai/generate-field`,
      { project_id: projectId, field, language, use_library: true },
      { params: { user_id: userId }, timeout: 60000 });
    return data;
  },

  // Plagiarism / semantic similarity check
  plagiarismCheck: async (userId, projectId, { text, chapterId, threshold = 0.92 } = {}) => {
    const { data } = await axios.post(`${API}/quality/plagiarism-check`,
      { project_id: projectId, text: text || '', chapter_id: chapterId || null, threshold },
      { params: { user_id: userId }, timeout: 120000 });
    return data;
  },

  // Multi-style citation export
  formatCitations: async (userId, projectId, style = 'vancouver', chapterIds = []) => {
    const { data } = await axios.post(`${API}/citations/format`,
      { project_id: projectId, style, chapter_ids: chapterIds, sort: true },
      { params: { user_id: userId }, timeout: 90000 });
    return data;
  },

  // PubMed v2 advanced search
  pubmedSearchV2: async (userId, payload) => {
    const { data } = await axios.post(`${API}/pubmed/search-v2`, payload,
      { params: { user_id: userId }, timeout: 60000 });
    return data;
  },
  pubmedMeshSuggest: async (userId, query) => {
    const { data } = await axios.get(`${API}/pubmed/mesh-suggest`,
      { params: { user_id: userId, query }, timeout: 30000 });
    return data;
  },

  // ============================================================================
  // SPRINT 1 — AI STRUCTURE BUILDER + CHAPTER-SPECIFIC LIBRARIES
  // ============================================================================

  // AI Structure Builder — propose a chapter architecture for the dissertation
  // Sprint 6 — Now uses background-job pattern to avoid K8s ingress timeouts.
  // Returns {job_id, status: "queued"} immediately. Poll via getStructureJob.
  suggestDissertationStructure: async (userId, projectId, overrides = {}) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/suggest-structure`,
      overrides,
      { params: { user_id: userId }, timeout: 30000 });
    return data;
  },

  // Poll a structure-builder job. Returns {status, progress, error?,
  // chapters?, methodology_notes?, suggested_guideline_id?}.
  getStructureJob: async (userId, jobId) => {
    const { data } = await axios.get(`${API}/structure-jobs/${jobId}`,
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  // Cancel a stuck `premium_status='running'` chapter — flips it to failed
  // so the polling loop disengages.
  cancelChapterGeneration: async (userId, chapterId) => {
    const { data } = await axios.post(`${API}/chapters/${chapterId}/cancel`,
      null, { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  listStructureTemplates: async (userId, projectType) => {
    const { data } = await axios.get(`${API}/structure-templates`,
      { params: { user_id: userId, project_type: projectType } });
    return data;
  },

  saveStructureTemplate: async (userId, payload) => {
    const { data } = await axios.post(`${API}/structure-templates`, payload,
      { params: { user_id: userId } });
    return data;
  },

  deleteStructureTemplate: async (userId, templateId) => {
    const { data } = await axios.delete(`${API}/structure-templates/${templateId}`,
      { params: { user_id: userId } });
    return data;
  },

  reverifyChapter: async (userId, chapterId) => {
    const { data } = await axios.post(`${API}/chapters/${chapterId}/reverify`,
      null, { params: { user_id: userId }, timeout: 120000 });
    return data;
  },

  reindexProject: async (userId, projectId) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/reindex`,
      null, { params: { user_id: userId }, timeout: 600000 });
    return data;
  },

  resetScreeningHistory: async (userId, sessionId) => {
    const { data } = await axios.post(`${SCREEN}/sessions/${sessionId}/reset-history`,
      null, { params: { user_id: userId } });
    return data;
  },

  // One-time backfill — assigns a unique monotonically-increasing `order` to
  // every chapter in the project. Safe to call idempotently on every load.
  normalizeChapterOrder: async (userId, projectId) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/chapters/normalize-order`,
      null, { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  // Backfill citations with bibliographic metadata (authors/journal/year)
  // pulled from thesis_documents. Lets the Vancouver-style "Sources" footer
  // render correctly for chapters created before Sprint 6.
  enrichChapterCitations: async (userId, projectId) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/chapters/enrich-citations`,
      null, { params: { user_id: userId }, timeout: 20000 });
    return data;
  },

  // ─── Wave D: Statistical / Gap / Novelty analyzers ──────────────────────
  startWaveDJob: async (userId, kind, payload) => {
    // kind = 'statistical-check' | 'gap-detect' | 'novelty-check'
    const { data } = await axios.post(`${API}/wave-d/${kind}`, payload,
      { params: { user_id: userId }, timeout: 30000 });
    return data;
  },

  pollWaveDJob: async (userId, jobId) => {
    const { data } = await axios.get(`${API}/wave-d/jobs/${jobId}`,
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  // Pre-publish Readiness Score — runs Citation Verifier + EQUATOR + 3 Wave-D
  // analyzers in parallel and aggregates into a single 0-100 dashboard.
  startReadinessAudit: async (userId, payload) => {
    const { data } = await axios.post(`${API}/readiness/compute`, payload,
      { params: { user_id: userId }, timeout: 30000 });
    return data;
  },

  pollReadinessJob: async (userId, jobId) => {
    const { data } = await axios.get(`${API}/readiness/jobs/${jobId}`,
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  // Project-wide aggregated readiness overview — reads persisted last_readiness
  // snapshots from every chapter; no LLM call.
  getProjectReadinessOverview: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/readiness-overview`,
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  // Defense inline suggestions (Sprint 7) — Grammarly-style accept/reject layer.
  buildDefenseInlineSuggestions: async (userId, defenseId, language = 'en') => {
    const { data } = await axios.post(`${API}/defense/reports/${defenseId}/inline-suggestions`,
      null, { params: { user_id: userId, language }, timeout: 120000 });
    return data;
  },

  applyInlineSuggestion: async (userId, payload) => {
    const { data } = await axios.post(`${API}/defense/apply-inline-suggestion`, payload,
      { params: { user_id: userId }, timeout: 20000 });
    return data;
  },

  // Persist accepted structure as chapters
  applyDissertationStructure: async (userId, projectId, chapters, replaceExisting = false) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/apply-structure`,
      { chapters, replace_existing: replaceExisting },
      { params: { user_id: userId }, timeout: 30000 });
    return data;
  },

  // Step 36 — Mine a published dissertation PDF for its TOC and propose
  // the same hierarchy as a starting structure for this project.
  importReferenceStructure: async (userId, projectId, file, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await axios.post(
      `${API}/projects/${projectId}/import-reference-structure`,
      form,
      {
        params: { user_id: userId },
        timeout: 60000,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round(100 * e.loaded / e.total));
        },
      });
    return data;
  },

  // Drag-and-drop chapter reorder (bulk update)
  reorderChapters: async (userId, projectId, chapterOrders) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/chapters/reorder`,
      { chapter_orders: chapterOrders },
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  // Assign a document to one or more chapters (chapter-specific library)
  assignDocumentToChapters: async (userId, projectId, docId, chapterIds) => {
    const { data } = await axios.put(`${API}/projects/${projectId}/documents/${docId}/chapters`,
      { chapter_ids: chapterIds },
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  // List documents attached to a specific chapter
  listChapterDocuments: async (userId, chapterId) => {
    const { data } = await axios.get(`${API}/chapters/${chapterId}/documents`,
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  // ============================================================================
  // SPRINT 2 — AI SCIENTIFIC COPILOT (multi-turn chat over project corpus)
  // ============================================================================

  copilotChat: async (userId, payload) => {
    const { data } = await axios.post(`${API}/copilot/chat`, payload,
      { params: { user_id: userId }, timeout: 90000 });
    return data;
  },

  listCopilotSessions: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/copilot/sessions`,
      { params: { user_id: userId, project_id: projectId }, timeout: 15000 });
    return data;
  },

  getCopilotSession: async (userId, projectId, sessionId) => {
    const { data } = await axios.get(`${API}/copilot/sessions/${sessionId}`,
      { params: { user_id: userId, project_id: projectId }, timeout: 15000 });
    return data;
  },

  deleteCopilotSession: async (userId, projectId, sessionId) => {
    const { data } = await axios.delete(`${API}/copilot/sessions/${sessionId}`,
      { params: { user_id: userId, project_id: projectId }, timeout: 15000 });
    return data;
  },

  // ============================================================================
  // SPRINT 3 — DEFENSE SIMULATION (5-reviewer panel)
  // ============================================================================

  simulateDefense: async (userId, chapterId, perspectives = null, language = 'en') => {
    const { data } = await axios.post(`${API}/defense/simulate`,
      { chapter_id: chapterId, perspectives, language },
      { params: { user_id: userId }, timeout: 30000 });
    return data;
  },

  getDefenseReport: async (userId, defenseId) => {
    const { data } = await axios.get(`${API}/defense/reports/${defenseId}`,
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  listDefenseReports: async (userId, chapterId) => {
    const { data } = await axios.get(`${API}/chapters/${chapterId}/defense-reports`,
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  // ============================================================================
  // SPRINT 4 — PREMIUM REASONING (Claim Grounding + Self-Feedback)
  // ============================================================================

  claimGrounding: async (userId, chapterId, language = 'en') => {
    const { data } = await axios.post(`${API}/premium/claim-grounding`,
      { chapter_id: chapterId, language },
      { params: { user_id: userId }, timeout: 30000 });
    return data;
  },

  getGroundingReport: async (userId, groundingId) => {
    const { data } = await axios.get(`${API}/grounding/reports/${groundingId}`,
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  selfFeedbackRefine: async (userId, chapterId, language = 'en') => {
    const { data } = await axios.post(`${API}/premium/self-feedback`,
      { chapter_id: chapterId, language },
      { params: { user_id: userId }, timeout: 30000 });
    return data;
  },

  listGroundingReports: async (userId, chapterId) => {
    const { data } = await axios.get(`${API}/chapters/${chapterId}/grounding-reports`,
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  // ============================================================================
  // SPRINT 5 CONTINUED — SUB-SECTIONS HIERARCHY + RESEARCH MEMORY
  // ============================================================================

  setChapterHierarchy: async (userId, chapterId, parentId = null, newOrder = null) => {
    const { data } = await axios.post(`${API}/chapters/${chapterId}/hierarchy`,
      { chapter_id: chapterId, parent_id: parentId, new_order: newOrder },
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  getProjectMemory: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/memory`,
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  refreshProjectMemory: async (userId, projectId, runLlm = true) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/memory/refresh`,
      {},
      { params: { user_id: userId, run_llm: runLlm }, timeout: 90000 });
    return data;
  },

  updateProjectMemory: async (userId, projectId, payload) => {
    const { data } = await axios.put(`${API}/projects/${projectId}/memory`,
      payload,
      { params: { user_id: userId }, timeout: 15000 });
    return data;
  },

  // Inline Evidence Inspector — uses existing /evidence/retrieve endpoint
  evidenceSearch: async (userId, projectId, query, options = {}) => {
    const { mode = 'hybrid', n_results = 10 } = options;
    const { data } = await axios.post(`${API}/evidence/retrieve`,
      { project_id: projectId, query, mode, n_results },
      { params: { user_id: userId }, timeout: 30000 });
    return data;
  },

  // AI Coach — what to work on next
  coachRecommendations: async (userId, projectId, includeLlm = true, language = 'en') => {
    const { data } = await axios.post(`${API}/coach/recommendations`,
      { project_id: projectId, include_llm_explanation: includeLlm, language },
      { params: { user_id: userId }, timeout: 60000 });
    return data;
  },
  // ==========================================================================
  // CODE PROJECT MODULE — "Article from a Python project"
  // ==========================================================================
  connectGithubProject: async (userId, projectId, { repoUrl, ref = '', token = '' }) => {
    const { data } = await axios.post(`${API}/code-projects/connect-github`,
      { project_id: projectId, repo_url: repoUrl, ref: ref || null, token: token || null },
      { params: { user_id: userId }, timeout: 180000 });
    return data;
  },
  uploadCodeZip: async (userId, projectId, file, onProgress) => {
    const form = new FormData();
    form.append('project_id', projectId);
    form.append('file', file);
    const { data } = await axios.post(`${API}/code-projects/upload-zip`, form, {
      params: { user_id: userId },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    return data;
  },
  listCodeProjects: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/code-projects`, {
      params: { user_id: userId },
    });
    return data;
  },
  getCodeProject: async (userId, cpId) => {
    const { data } = await axios.get(`${API}/code-projects/${cpId}`, { params: { user_id: userId } });
    return data;
  },
  deleteCodeProject: async (userId, cpId) => {
    const { data } = await axios.delete(`${API}/code-projects/${cpId}`, { params: { user_id: userId } });
    return data;
  },
  listCodeFiles: async (userId, cpId, { q = '', selectedOnly = false } = {}) => {
    const { data } = await axios.get(`${API}/code-projects/${cpId}/files`, {
      params: { user_id: userId, q, selected_only: selectedOnly },
    });
    return data;
  },
  selectCodeComponent: async (userId, cpId, { componentKeys = [], extraPaths = [], componentName = '' }) => {
    const { data } = await axios.post(`${API}/code-projects/${cpId}/select-component`,
      { component_keys: componentKeys, extra_paths: extraPaths, component_name: componentName || null },
      { params: { user_id: userId }, timeout: 120000 });
    return data;
  },
  extractCodeFacts: async (userId, cpId, language = 'en') => {
    const { data } = await axios.post(`${API}/code-projects/${cpId}/extract-facts`,
      { language }, { params: { user_id: userId }, timeout: 60000 });
    return data;
  },
  listCodeClaims: async (userId, cpId) => {
    const { data } = await axios.get(`${API}/code-projects/${cpId}/claims`, { params: { user_id: userId } });
    return data;
  },
  getCodeClaimSource: async (userId, cpId, claimId) => {
    const { data } = await axios.get(`${API}/code-projects/${cpId}/claims/${claimId}/source`,
      { params: { user_id: userId } });
    return data;
  },
  codeTitleIdeas: async (userId, cpId, language = 'en') => {
    const { data } = await axios.post(`${API}/code-projects/${cpId}/title-ideas`,
      { language }, { params: { user_id: userId }, timeout: 180000 });
    return data;
  },
  startCodeDraft: async (userId, cpId, { section, language = 'en', extraInstructions = '' }) => {
    const { data } = await axios.post(`${API}/code-projects/${cpId}/draft-section`,
      { section, language, extra_instructions: extraInstructions },
      { params: { user_id: userId }, timeout: 60000 });
    return data;
  },
  getCodeJob: async (userId, cpId, jobId) => {
    const { data } = await axios.get(`${API}/code-projects/${cpId}/jobs/${jobId}`,
      { params: { user_id: userId } });
    return data;
  },
  insertCodeDraft: async (userId, cpId, { title, markdown, chapterId = null, mode = 'new' }) => {
    const { data } = await axios.post(`${API}/code-projects/${cpId}/insert-draft`,
      { title, markdown, chapter_id: chapterId, mode },
      { params: { user_id: userId }, timeout: 60000 });
    return data;
  },
  resyncCodeProject: async (userId, cpId, { ref = '', token = '' } = {}) => {
    const { data } = await axios.post(`${API}/code-projects/${cpId}/resync`,
      { ref: ref || null, token: token || null },
      { params: { user_id: userId }, timeout: 180000 });
    return data;
  },
  suggestCodeComponent: async (userId, cpId) => {
    const { data } = await axios.post(`${API}/code-projects/${cpId}/suggest-component`, {},
      { params: { user_id: userId }, timeout: 180000 });
    return data;
  },
  startCodeFigure: async (userId, cpId, language = 'en') => {
    const { data } = await axios.post(`${API}/code-projects/${cpId}/architecture-figure`,
      { language }, { params: { user_id: userId }, timeout: 60000 });
    return data;
  },
  insertCodeFigure: async (userId, cpId, { figureId, caption, chapterId = null }) => {
    const { data } = await axios.post(`${API}/code-projects/${cpId}/insert-figure`,
      { figure_id: figureId, caption, chapter_id: chapterId, mode: chapterId ? 'append' : 'new' },
      { params: { user_id: userId }, timeout: 60000 });
    return data;
  },
  codeClaimUsage: async (userId, cpId) => {
    const { data } = await axios.get(`${API}/code-projects/${cpId}/claim-usage`,
      { params: { user_id: userId } });
    return data;
  },
  codeSoftwareReference: async (userId, cpId, style = 'vancouver') => {
    const { data } = await axios.get(`${API}/code-projects/${cpId}/software-reference`,
      { params: { user_id: userId, style } });
    return data;
  },
  findCodeClaimReference: async (userId, cpId, claimId) => {
    const { data } = await axios.post(
      `${API}/code-projects/${cpId}/claims/${claimId}/find-reference`, {},
      { params: { user_id: userId }, timeout: 180000 });
    return data;
  },
  attachCodeClaimReference: async (userId, cpId, claimId, { pmid, chapterId = null }) => {
    const { data } = await axios.post(
      `${API}/code-projects/${cpId}/claims/${claimId}/attach-reference`,
      { pmid, chapter_id: chapterId },
      { params: { user_id: userId }, timeout: 180000 });
    return data;
  },

  // FIGURES (user uploads)
  listFigures: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/figures`,
      { params: { user_id: userId } });
    return data;
  },
  uploadFigure: async (userId, projectId, file, caption = '', sourceNote = '') => {
    const form = new FormData();
    form.append('file', file);
    form.append('caption', caption);
    form.append('source_note', sourceNote);
    const { data } = await axios.post(`${API}/projects/${projectId}/figures`, form,
      { params: { user_id: userId }, timeout: 300000 });
    return data;
  },
  insertFigure: async (userId, figureId, chapterId = null, caption = null) => {
    const { data } = await axios.post(`${API}/figures/${figureId}/insert`,
      { chapter_id: chapterId, caption }, { params: { user_id: userId } });
    return data;
  },
  insertFigureReference: async (userId, figureId, chapterId) => {
    const { data } = await axios.post(`${API}/figures/${figureId}/insert-reference`,
      { chapter_id: chapterId }, { params: { user_id: userId } });
    return data;
  },
  moveFigure: async (userId, figureId, direction) => {
    const { data } = await axios.post(`${API}/figures/${figureId}/move`, { direction },
      { params: { user_id: userId } });
    return data;
  },
  insertTableReference: async (userId, tableId, chapterId) => {
    const { data } = await axios.post(`${API}/tables/${tableId}/insert-reference`,
      { chapter_id: chapterId }, { params: { user_id: userId } });
    return data;
  },
  moveTable: async (userId, tableId, direction) => {
    const { data } = await axios.post(`${API}/tables/${tableId}/move`, { direction },
      { params: { user_id: userId } });
    return data;
  },
  getAssetLabels: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/asset-labels`,
      { params: { user_id: userId } });
    return data;
  },
  datasetSurvival: async (userId, datasetId, payload) => {
    const { data } = await axios.post(`${API}/datasets/${datasetId}/survival`, payload,
      { params: { user_id: userId }, timeout: 240000 });
    return data;
  },
  datasetRegression: async (userId, datasetId, payload) => {
    const { data } = await axios.post(`${API}/datasets/${datasetId}/regression`, payload,
      { params: { user_id: userId }, timeout: 240000 });
    return data;
  },
  datasetRoc: async (userId, datasetId, payload) => {
    const { data } = await axios.post(`${API}/datasets/${datasetId}/roc`, payload,
      { params: { user_id: userId }, timeout: 240000 });
    return data;
  },
  datasetCorrelation: async (userId, datasetId, payload) => {
    const { data } = await axios.post(`${API}/datasets/${datasetId}/correlation`, payload,
      { params: { user_id: userId }, timeout: 240000 });
    return data;
  },
  statisticsMethodsParagraph: async (userId, projectId, chapterId = null) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/statistics-methods`,
      { chapter_id: chapterId }, { params: { user_id: userId }, timeout: 120000 });
    return data;
  },
  deleteFigure: async (userId, figureId) => {
    const { data } = await axios.delete(`${API}/figures/${figureId}`, { params: { user_id: userId } });
    return data;
  },

  // NUMBERED TABLES
  listTables: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/tables`,
      { params: { user_id: userId } });
    return data;
  },
  saveTable: async (userId, projectId, { caption = '', markdown, note = '', source = 'manual' }) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/tables`,
      { caption, markdown, note, source }, { params: { user_id: userId } });
    return data;
  },
  insertTable: async (userId, tableId, chapterId = null, caption = null) => {
    const { data } = await axios.post(`${API}/tables/${tableId}/insert`,
      { chapter_id: chapterId, caption }, { params: { user_id: userId } });
    return data;
  },
  deleteTable: async (userId, tableId) => {
    const { data } = await axios.delete(`${API}/tables/${tableId}`, { params: { user_id: userId } });
    return data;
  },

  // REFERENCE IMPORT
  importReferences: async (userId, projectId, file) => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await axios.post(`${API}/projects/${projectId}/references/import`, form,
      { params: { user_id: userId }, timeout: 300000 });
    return data;
  },
  listReferences: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/references`,
      { params: { user_id: userId } });
    return data;
  },
  deleteReference: async (userId, referenceId) => {
    const { data } = await axios.delete(`${API}/references/${referenceId}`,
      { params: { user_id: userId } });
    return data;
  },

  // DATASETS / STATISTICS
  uploadDataset: async (userId, projectId, file, label = '') => {
    const form = new FormData();
    form.append('file', file);
    form.append('label', label);
    const { data } = await axios.post(`${API}/projects/${projectId}/datasets`, form,
      { params: { user_id: userId }, timeout: 300000 });
    return data;
  },
  listDatasets: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/datasets`,
      { params: { user_id: userId } });
    return data;
  },
  deleteDataset: async (userId, datasetId) => {
    const { data } = await axios.delete(`${API}/datasets/${datasetId}`,
      { params: { user_id: userId } });
    return data;
  },
  describeDataset: async (userId, datasetId, groupBy = null, columns = null) => {
    const { data } = await axios.post(`${API}/datasets/${datasetId}/describe`,
      { group_by: groupBy, columns }, { params: { user_id: userId }, timeout: 180000 });
    return data;
  },

  // FRONT MATTER
  getFrontMatter: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/front-matter`,
      { params: { user_id: userId } });
    return data;
  },
  saveFrontMatter: async (userId, projectId, payload) => {
    const { data } = await axios.put(`${API}/projects/${projectId}/front-matter`, payload,
      { params: { user_id: userId } });
    return data;
  },
  detectAbbreviations: async (userId, projectId) => {
    const { data } = await axios.post(`${API}/projects/${projectId}/front-matter/detect-abbreviations`,
      {}, { params: { user_id: userId } });
    return data;
  },
};
